import React from "react";
import styled from "styled-components";
import colours from "../../lib/colours";
import appConfig from "../../lib/appConfig";
import CloseButton from "../atoms/CloseButton";
import FilledButton, { FilledButtonAlt } from "../atoms/FilledButton";

const StyledOverlay = styled.div`
  position: fixed;
  height: 100%;
  width: 100%;
  max-height: 100%;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: #000;
  opacity: 0.5;
  z-index: 10;
`;

const StyledContainer = styled.div`
  position: fixed;
  transform: translate(-50%, -50%);
  top: 50%;
  left: 50%;
  min-width: 200px;
  max-width: 90%;
  max-height: 90%;
  background-color: ${colours.white};
  z-index: 10;
  box-shadow: 0px 0px 12px 0px rgba(0, 0, 0, 0.25);
  display: flex;
  flex-direction: column;
  @media (max-width: ${appConfig.tabletWidth}px) {
    max-width: 100%;
    max-height: 100%;
    width: 100%;
  }
`;

const StyledMapDialogContainer = styled.div`
  position: absolute;
  top: 115px;
  left: 50%;
  transform: translate(-50%);
  min-width: 200px;
  width: 500px;
  max-width: min(80%, 700px);
  max-height: 78%;
  background-color: ${colours.white};
  box-shadow: 0px 0px 12px 0px rgba(0, 0, 0, 0.25);
  display: flex;
  flex-direction: column;
  @media (max-width: ${appConfig.tabletWidth}px) {
    max-width: 100%;
    width: 100%;
    top: 0;
    max-height: 100%;
    z-index: 6;
  }
  @media (max-height: 725px) {
    top: 152px;
    max-height: 66%;
    z-index: 3;
  }
  @media (max-height: 725px) and (max-width: ${appConfig.tabletWidth}px) {
    top: 0;
    max-height: 100%;
    z-index: 6;
  }
`;

const StyledHeadingContainer = styled.div`
  text-align: left;
  padding: 20px 20px 15px 28px;
  padding: 12px 20px 10px 28px;
  display: flex;
  color: ${colours.th1};
  background-color: ${colours.offWhite};
  align-items: baseline;
`;

const StyledHeading = styled.h1`
  font-weight: 400;
  font-size: 18px;
  color: ${colours.th1};
  line-height: 37px;
  margin: 0;
  text-transform: uppercase;
`;

const StyledContentContainer = styled.div`
  text-align: left;
  padding: 20px 28px;
  color: ${colours.darkerGrey};
  overflow: auto;
  display: flex;
  flex-direction: column;
`;

const StyledFooterContainer = styled.div`
  display: flex;
  flex-direction: row-reverse;
  padding: 15px 20px 15px 28px;
  color: ${colours.th1};
`;

const DialogFilledButton = styled(FilledButton)`
  height: auto;
`;

const DialogFilledButtonAlt = styled(FilledButtonAlt)`
  height: auto;
`;

export interface IProps {
  container?: string;
  open: boolean;
  onClose?: any;
  modal?: boolean;
  transparentOverlay?: boolean;
  showHeader?: boolean;
  showFooter?: boolean;
  showXButton?: boolean;
  heading?: string;
  children: any;
  additionalButtons?: any[];
  closeButtonText?: string;
}

class Dialog extends React.Component<IProps> {
  static defaultProps: {
    container: string;
    modal: boolean;
    transparentOverlay: boolean;
    showHeader: boolean;
    showFooter: boolean;
    showXButton: boolean;
    onClose: () => void;
    closeButtonText: string;
  };

  render() {
    const {
      container,
      open,
      onClose,
      modal,
      transparentOverlay,
      showHeader,
      showFooter,
      showXButton,
      heading,
      children,
      additionalButtons,
      closeButtonText,
    } = this.props;
    const ContainerElement =
      container === "map" ? StyledMapDialogContainer : StyledContainer;
    return (
      <>
        <StyledOverlay
          id="dialog-overlay"
          onClick={() => !modal && onClose()}
          style={{
            visibility: open ? "visible" : "hidden",
            background: transparentOverlay ? "transparent" : "#000",
            zIndex: container === "map" ? 0 : 10,
            position: container === "map" ? "absolute" : "fixed",
          }}
        ></StyledOverlay>
        <ContainerElement
          onClick={(e) => {
            e.stopPropagation();
          }}
          style={{
            visibility: open ? "visible" : "hidden",
          }}
        >
          {showHeader && (
            <StyledHeadingContainer>
              <StyledHeading>{heading}</StyledHeading>
              {showXButton && <CloseButton onClick={() => onClose()} />}
            </StyledHeadingContainer>
          )}
          <StyledContentContainer>{children}</StyledContentContainer>
          {showFooter && (
            <StyledFooterContainer>
              <DialogFilledButtonAlt onClick={() => onClose()}>
                {closeButtonText}
              </DialogFilledButtonAlt>
              {additionalButtons
                ? additionalButtons.map((additionalButton: any) => {
                    const ButtonElement = additionalButton.primary
                      ? DialogFilledButton
                      : DialogFilledButtonAlt;
                    return (
                      <div
                        key={`textButton${additionalButton.text}`}
                        style={{ marginRight: "10px" }}
                      >
                        <ButtonElement
                          onClick={additionalButton.onClickHandler}
                          disabled={additionalButton.disabled}
                        >
                          {additionalButton.text}
                        </ButtonElement>
                      </div>
                    );
                  })
                : null}
            </StyledFooterContainer>
          )}
        </ContainerElement>
      </>
    );
  }
}

Dialog.defaultProps = {
  container: "app",
  modal: true,
  transparentOverlay: false,
  showHeader: true,
  showFooter: true,
  showXButton: true,
  onClose: () => {},
  closeButtonText: "Close",
};

export default Dialog;
